import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * Returns whether the currently signed-in user has their profile hidden.
 * Users with hidden profiles cannot send friend requests ("Say hi").
 */
export const useMyProfileHidden = () => {
  const { user } = useAuth();
  const [hidden, setHidden] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!user) {
        setHidden(false);
        setLoading(false);
        return;
      }
      const { data } = await supabase
        .from("profiles")
        .select("profile_hidden")
        .eq("id", user.id)
        .maybeSingle();
      if (!cancelled) {
        setHidden(!!data?.profile_hidden);
        setLoading(false);
      }
    };
    load();

    // Refresh when the profile changes (e.g. user toggles visibility in Settings)
    const channel = supabase
      .channel(`profile-hidden-${user?.id ?? "anon"}-${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "profiles", filter: user ? `id=eq.${user.id}` : undefined },
        (payload: any) => {
          setHidden(!!payload.new?.profile_hidden);
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  return { hidden, loading };
};
